import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ShoppingBag, User, LogOut, LayoutDashboard } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";

export default function Navbar() {
  const { user, isAdmin, logout } = useAuth();
  const { count, setOpen } = useCart();
  const navigate = useNavigate();

  return (
    <motion.header
      initial={{ y: -70, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
      className="fixed top-9 inset-x-0 z-50 bg-white/95 backdrop-blur-md border-b border-slate-200 shadow-sm"
    >
      <div className="max-w-[1400px] mx-auto px-5 sm:px-10 h-16 flex items-center justify-between">
        <Link to="/" data-testid="nav-brand-logo" className="flex items-center gap-2.5 group">
          <img src="/logo.jpeg" alt="Kanan Remedies logo" className="h-11 w-11 rounded-full object-cover ring-1 ring-slate-200 shadow-sm transition-transform duration-300 group-hover:scale-105" />
          <span className="font-display font-bold tracking-tight text-lg text-[#2A6525]">KananRemedies</span>
        </Link>

        <nav className="hidden md:flex items-center gap-8 font-mono-ed text-[11px] uppercase tracking-[0.2em] text-slate-500">
          <Link to="/shop" data-testid="nav-link-shop" className="hover:text-[#0F5A47] transition-colors duration-300">Catalog</Link>
          <Link to="/shop?rx=true" data-testid="nav-link-rx" className="hover:text-[#0F5A47] transition-colors duration-300">Rx Medicines</Link>
          <Link to="/orders" data-testid="nav-link-orders" className="hover:text-[#0F5A47] transition-colors duration-300">Orders</Link>
          {isAdmin && (
            <Link to="/admin" data-testid="nav-link-admin" className="flex items-center gap-1.5 text-[#1D4ED8] hover:text-[#0F5A47] transition-colors duration-300">
              <LayoutDashboard size={13} /> Admin
            </Link>
          )}
        </nav>

        <div className="flex items-center gap-2">
          {user ? (
            <button
              data-testid="nav-logout-btn"
              onClick={async () => { await logout(); navigate("/"); }}
              className="hidden sm:flex items-center gap-1.5 font-mono-ed text-[11px] uppercase tracking-[0.15em] text-slate-500 hover:text-red-600 transition-colors px-3 py-2"
              title={`Signed in as ${user.email}`}
            >
              <LogOut size={13} /> Exit
            </button>
          ) : (
            <Link
              to="/login"
              data-testid="nav-login-btn"
              className="hidden sm:flex items-center gap-1.5 font-mono-ed text-[11px] uppercase tracking-[0.15em] text-slate-500 hover:text-[#0F5A47] transition-colors px-3 py-2"
            >
              <User size={13} /> Sign In
            </Link>
          )}
          <button
            data-testid="nav-cart-trigger"
            onClick={() => setOpen(true)}
            className="relative flex items-center gap-2 bg-emerald-800 text-white px-4 py-2.5 rounded-xl font-bold text-xs hover:bg-emerald-900 transition-colors duration-300 active:scale-95"
          >
            <ShoppingBag size={14} />
            Cart
            {count > 0 && (
              <span data-testid="nav-cart-badge" className="absolute -top-2 -right-2 w-5 h-5 bg-[#1D4ED8] text-white text-[10px] flex items-center justify-center rounded-full">{count}</span>
            )}
          </button>
        </div>
      </div>
    </motion.header>
  );
}
